import React, { useState, useEffect } from 'react';
import { ExternalLink, Lightbulb, Plus, X, Smile, RefreshCw, ChevronDown, Clock, Users } from 'lucide-react';
import EmotePicker from '../EmotePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Producer tip categories for filtering - Compact names
const PRODUCER_FILTER_CATEGORIES = [
  { id: 'all', name: 'All Tips' },
  { id: 'mixing', name: 'Mixing', keywords: ['mix', 'mixing', 'master', 'mastering', 'EQ', 'compression', 'limiter', 'loudness', 'gain staging'] },
  { id: 'sound-design', name: 'Sound Design', keywords: ['sound design', 'synth', 'synthesis', 'wavetable', 'FM', 'granular', 'patch', 'preset', 'oscillator'] },
  { id: 'daw', name: 'DAW Tips', keywords: ['ableton', 'fl studio', 'logic', 'pro tools', 'cubase', 'bitwig', 'DAW', 'workflow', 'shortcut', 'template'] },
  { id: 'arrangement', name: 'Arrangement', keywords: ['arrangement', 'structure', 'intro', 'outro', 'drop', 'breakdown', 'build', 'tension', 'energy'] },
  { id: 'plugins', name: 'Plugins', keywords: ['plugin', 'VST', 'serum', 'massive', 'kontakt', 'omnisphere', 'fabfilter', 'waves', 'izotope'] },
  { id: 'creative', name: 'Creative', keywords: ['creative', 'technique', 'trick', 'hack', 'secret', 'pro tip', 'advanced'] }
];

// DJ tip categories for filtering
const DJ_FILTER_CATEGORIES = [
  { id: 'all', name: 'All Tips' },
  { id: 'beatmatching', name: 'Beatmatching', keywords: ['beatmatch', 'beatmatching', 'beat match', 'sync', 'tempo', 'BPM', 'pitch', 'pitch fader', 'manual beat'] },
  { id: 'mixing', name: 'Mixing', keywords: ['mix', 'mixing', 'blend', 'transition', 'crossfade', 'EQ', 'levels', 'gain', 'volume', 'long blend', 'smooth mix'] },
  { id: 'transitions', name: 'Transitions', keywords: ['transition', 'drop swap', 'echo out', 'cut', 'slam', 'backspin', 'spinback', 'fade', 'phrase', 'phrasing'] },
  { id: 'scratching', name: 'Scratching', keywords: ['scratch', 'scratching', 'turntablism', 'chirp', 'flare', 'crab', 'transformer', 'juggle', 'routine'] },
  { id: 'set-building', name: 'Set Building', keywords: ['set', 'setlist', 'playlist', 'track selection', 'song selection', 'reading the crowd', 'crowd', 'energy', 'journey', 'programming', 'warm up', 'peak time', 'closing'] },
  { id: 'gear', name: 'Gear & Setup', keywords: ['controller', 'CDJ', 'turntable', 'mixer', 'headphones', 'monitor', 'setup', 'gear', 'equipment', 'rekordbox', 'serato', 'traktor', 'pioneer', 'denon', 'technics'] },
  { id: 'harmonic', name: 'Harmonic Mixing', keywords: ['harmonic', 'key', 'camelot', 'musical key', 'key mixing', 'compatible key', 'open key', 'key clash'] },
  { id: 'streaming', name: 'Streaming', keywords: ['stream', 'streaming', 'twitch', 'OBS', 'broadcast', 'live stream', 'overlay', 'webcam', 'audio routing'] },
];

// STRICT keywords to identify tips/tutorials content - excludes news articles
const TIP_CONTENT_KEYWORDS = [
  'tip', 'tips', 'trick', 'tricks', 'tutorial', 'how to', 'guide', 
  'technique', 'secret', 'hack', 'workflow', 'step by step', 
  'beginner', 'advanced', 'pro tip', 'quick tip',
  'learn', 'masterclass', 'mixing tip', 'production',
  'ways to', 'improve', 'better', 'essential'
];

// Keywords that indicate NEWS (not tips) - exclude these
const NEWS_EXCLUSION_KEYWORDS = [
  'announces', 'announced', 'festival lineup', 'tour dates',
  'live set at', 'b2b set', 'performs at', 'headlines',
  'wins award', 'nominated for', 'chart position'
];

const ProTipsTab = ({ mode = 'producer' }) => {
  const TIP_FILTER_CATEGORIES = mode === 'dj' ? DJ_FILTER_CATEGORIES : PRODUCER_FILTER_CATEGORIES;
  const [activeTab, setActiveTab] = useState('industry'); // 'industry' or 'community'
  
  // Industry Tips state
  const [industryTips, setIndustryTips] = useState([]);
  const [industryLoading, setIndustryLoading] = useState(true);
  const [industryRefreshing, setIndustryRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Community Tips state
  const [communityTips, setCommunityTips] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [showNewTipModal, setShowNewTipModal] = useState(false);
  const [newTipTitle, setNewTipTitle] = useState('');
  const [newTipDescription, setNewTipDescription] = useState('');
  const [newTipType, setNewTipType] = useState('tutorial');
  const [newTipTags, setNewTipTags] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showEmotePicker, setShowEmotePicker] = useState(false);

  useEffect(() => {
    if (activeTab === 'industry') {
      loadIndustryTips();
    } else {
      loadCommunityTips();
    }
  }, [activeTab]);

  const loadIndustryTips = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setIndustryRefreshing(true);
      } else {
        setIndustryLoading(true);
      }

      const response = await fetch(`${BACKEND_URL}/api/news/feed?force_refresh=${forceRefresh}`);
      
      if (response.ok) {
        const data = await response.json();
        const allNews = data.articles || data;
        
        // Filter for tips/tutorials content, including Digital DJ Tips
        const tips = filterTipsContent(allNews);
        
        // Sort by date
        const sortedTips = tips.sort((a, b) => 
          new Date(b.published || b.date) - new Date(a.published || a.date)
        );
        
        setIndustryTips(sortedTips);
      } else {
        setIndustryTips(getMockIndustryTips());
      }
    } catch (error) {
      console.error('Error loading industry tips:', error);
      setIndustryTips(getMockIndustryTips());
    } finally {
      setIndustryLoading(false);
      setIndustryRefreshing(false);
    }
  };

  const filterTipsContent = (articles) => {
    return articles.filter(article => {
      const title = (article.title || '').toLowerCase();
      const summary = (article.summary || article.description || '').toLowerCase();
      const searchText = `${title} ${summary}`;
      const source = (article.source || '').toLowerCase();
      
      // Include Digital DJ Tips source (prioritize this source for tips)
      const isDigitalDJTips = source.includes('digital dj tips');
      
      // Check if content matches tip/tutorial keywords (STRICT matching)
      const hasTipKeywords = TIP_CONTENT_KEYWORDS.some(keyword => 
        title.includes(keyword.toLowerCase()) || summary.includes(keyword.toLowerCase())
      );
      
      // Exclude news articles by checking exclusion keywords
      const isNewsArticle = NEWS_EXCLUSION_KEYWORDS.some(keyword => 
        title.includes(keyword.toLowerCase())
      );
      
      // Include if: (is Digital DJ Tips OR has tip keywords) AND is NOT a news article
      return (isDigitalDJTips || hasTipKeywords) && !isNewsArticle;
    });
  };

  const getFilteredIndustryTips = () => {
    let filtered = industryTips;
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      const category = TIP_FILTER_CATEGORIES.find(c => c.id === selectedCategory);
      if (category && category.keywords) {
        filtered = filtered.filter(tip => {
          const searchText = `${tip.title || ''} ${tip.summary || ''} ${tip.description || ''}`.toLowerCase();
          return category.keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
        });
      }
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tip => {
        const searchText = `${tip.title || ''} ${tip.summary || ''} ${tip.description || ''}`.toLowerCase();
        return searchText.includes(term);
      });
    }
    
    return filtered.slice(0, 30);
  };

  const getMockIndustryTips = () => [
    {
      id: 1,
      title: "10 Mixing Tips Every Producer Should Know",
      published: new Date().toISOString(),
      summary: "Essential mixing techniques to take your tracks to the next level.",
      source: "Digital DJ Tips",
      url: "#"
    },
    {
      id: 2,
      title: "Sound Design Secrets: Creating Unique Bass Sounds",
      published: new Date(Date.now() - 86400000).toISOString(),
      summary: "Learn how to craft punchy, unique bass sounds from scratch.",
      source: "MusicRadar",
      url: "#"
    }
  ];

  const loadCommunityTips = async () => {
    try {
      setCommunityLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/producer/tips`);
      if (response.ok) {
        const data = await response.json();
        setCommunityTips(data.length > 0 ? data : getDefaultCommunityTips());
      } else {
        setCommunityTips(getDefaultCommunityTips());
      }
    } catch (error) {
      console.error('Error loading community tips:', error);
      setCommunityTips(getDefaultCommunityTips());
    } finally {
      setCommunityLoading(false);
    }
  };

  const getDefaultCommunityTips = () => [
    {
      id: 1,
      title: "Mastering Electronic Music: EQ Techniques",
      author_name: "DJ Producer",
      created_at: "2025-10-04",
      description: "Essential EQ tips for getting your electronic tracks radio-ready.",
      type: "tutorial",
      tags: ["Mixing", "Mastering", "Electronic"]
    },
    {
      id: 2,
      title: "Creating Powerful Drop Sections",
      author_name: "BassMaster",
      created_at: "2025-10-03",
      description: "How to build energy and create impactful drops.",
      type: "technique",
      tags: ["Production", "Arrangement", "Bass"]
    }
  ];

  const handleEmoteSelect = (emoteCode) => {
    setNewTipDescription(prev => prev + emoteCode);
    setShowEmotePicker(false);
  };

  const handleNewTip = () => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to submit a tip');
      return;
    }
    setShowNewTipModal(true);
  };

  const submitNewTip = async () => {
    if (!newTipTitle.trim() || !newTipDescription.trim()) {
      alert('Please fill in both title and description');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const tagsArray = newTipTags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const response = await fetch(`${BACKEND_URL}/api/producer/tips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newTipTitle.trim(),
          description: newTipDescription.trim(),
          type: newTipType,
          tags: tagsArray
        })
      });

      if (response.ok) {
        const newTip = await response.json();
        
        if (newTip.moderation_status === 'pending_moderation') {
          alert('⏳ Your tip has been sent to admin for approval');
        } else {
          setCommunityTips(prevTips => [newTip, ...prevTips]);
          alert('Tip submitted successfully!');
        }
        
        resetForm();
      } else {
        const errorData = await response.json();
        alert(`Failed to submit tip: ${errorData.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error submitting tip:', error);
      alert('Error submitting tip. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowNewTipModal(false);
    setNewTipTitle('');
    setNewTipDescription('');
    setNewTipType('tutorial');
    setNewTipTags('');
    setShowEmotePicker(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const handleTipClick = (tip) => {
    if (tip.url && tip.url !== '#') {
      window.open(tip.url, '_blank');
    }
  };

  const filteredIndustryTips = getFilteredIndustryTips();

  return (
    <div className="pro-tips-tab">
      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => setActiveTab('industry')}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '10px',
              border: activeTab === 'industry' ? '1px solid #f59e0b' : '1px solid rgba(245, 158, 11, 0.3)',
              background: activeTab === 'industry' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(30, 20, 50, 0.5)',
              color: activeTab === 'industry' ? '#fbbf24' : 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Lightbulb size={16} /> Industry Tips
          </button>
          <button
            onClick={() => setActiveTab('community')}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '10px',
              border: activeTab === 'community' ? '1px solid #9333ea' : '1px solid rgba(147, 51, 234, 0.3)',
              background: activeTab === 'community' ? 'rgba(147, 51, 234, 0.2)' : 'rgba(30, 20, 50, 0.5)',
              color: activeTab === 'community' ? '#c4b5fd' : 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Users size={16} /> Beatseeker Tips
          </button>
        </div>

        {/* Action Button - Only for Community tab */}
        {activeTab === 'community' && (
          <button
            onClick={handleNewTip}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '10px',
              border: '1px solid #fbbf24',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(147, 51, 234, 0.2))',
              color: '#fbbf24',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={16} /> Post New Tip
          </button>
        )}
      </div>

      {/* Industry Tips Tab */}
      {activeTab === 'industry' && (
        <>
          {/* Filter Bar */}
          <div style={{ 
            display: 'flex', 
            gap: '0.75rem', 
            marginBottom: '1.5rem',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {/* Category Dropdown - Compact */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                background: 'rgba(30, 20, 50, 0.5)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.875rem',
                cursor: 'pointer',
                minWidth: '100px',
                maxWidth: '130px'
              }}
            >
              {TIP_FILTER_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            {/* Search Input - Takes more space */}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tips..."
              style={{
                flex: '1',
                minWidth: '200px',
                padding: '0.75rem 1rem',
                background: 'rgba(30, 20, 50, 0.5)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.875rem'
              }}
            />

            {/* Refresh Button */}
            <button
              onClick={() => loadIndustryTips(true)}
              disabled={industryRefreshing}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '10px',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                background: 'rgba(30, 20, 50, 0.5)',
                color: industryRefreshing ? 'rgba(255,255,255,0.4)' : '#fbbf24',
                cursor: industryRefreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <RefreshCw size={14} style={{ animation: industryRefreshing ? 'spin 1s linear infinite' : 'none' }} />
              {industryRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Filter Info */}
          <div style={{ 
            background: 'rgba(245, 158, 11, 0.1)', 
            border: '1px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            marginBottom: '1.5rem',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.6)'
          }}>
            💡 Showing {filteredIndustryTips.length} tips & tutorials from industry sources
          </div>

          {/* Tips List */}
          {industryLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid rgba(245,158,11,0.3)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading industry tips...</p>
              </div>
            </div>
          ) : filteredIndustryTips.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {filteredIndustryTips.map((tip) => (
                <div
                  key={tip.id}
                  onClick={() => handleTipClick(tip)}
                  style={{
                    background: 'rgba(30, 20, 50, 0.5)',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    cursor: tip.url ? 'pointer' : 'default',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.5)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ color: '#fff', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        {tip.title}
                      </h4>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                        {tip.summary || tip.description}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={12} /> {formatDate(tip.published || tip.date)}
                        </span>
                        <span style={{ background: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24', padding: '2px 8px', borderRadius: '4px' }}>
                          {tip.source}
                        </span>
                      </div>
                    </div>
                    {tip.url && <ExternalLink size={18} style={{ color: '#f59e0b', flexShrink: 0, marginLeft: '1rem' }} />}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <Lightbulb size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: '1rem' }} />
              <p style={{ color: 'rgba(255,255,255,0.5)' }}>No tips match your filters</p>
              {(searchTerm || selectedCategory !== 'all') && (
                <button
                  onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid #f59e0b',
                    background: 'transparent',
                    color: '#fbbf24',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Community Tips Tab */}
      {activeTab === 'community' && (
        <>
          {/* Info Banner */}
          <div style={{ 
            background: 'rgba(147, 51, 234, 0.1)', 
            border: '1px solid rgba(147, 51, 234, 0.2)',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            marginBottom: '1.5rem',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.6)'
          }}>
            🎧 Tips shared by fellow Beatseeker producers
          </div>

          {/* Tips List */}
          {communityLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid rgba(147,51,234,0.3)', borderTopColor: '#9333ea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading community tips...</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {communityTips.map((tip) => (
                <div
                  key={tip.id}
                  style={{
                    background: 'rgba(30, 20, 50, 0.5)',
                    border: '1px solid rgba(147, 51, 234, 0.2)',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.5)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ color: '#fff', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        {tip.title}
                      </h4>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                        {tip.description}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.75rem' }}>
                        <span>By {tip.author_name || tip.author}</span>
                        <span>{formatDate(tip.created_at || tip.date)}</span>
                        <span style={{ background: 'rgba(147, 51, 234, 0.3)', padding: '2px 8px', borderRadius: '4px', color: '#c4b5fd', textTransform: 'capitalize' }}>
                          {tip.type}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {(tip.tags || []).map((tag, index) => (
                          <span key={index} style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', padding: '4px 10px', borderRadius: '6px', fontSize: '0.7rem' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ExternalLink size={18} style={{ color: '#9333ea', flexShrink: 0, marginLeft: '1rem' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* New Tip Modal */}
      {showNewTipModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98))',
            border: '2px solid rgba(147, 51, 234, 0.4)',
            borderRadius: '16px',
            padding: '2rem',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '1.25rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Lightbulb size={20} /> Share Your Pro Tip
              </h3>
              <button
                onClick={resetForm}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={newTipTitle}
                  onChange={(e) => setNewTipTitle(e.target.value)}
                  placeholder="e.g., Mastering Bass Compression"
                  maxLength={150}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'rgba(30, 20, 50, 0.5)',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              <div>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                  Description *
                </label>
                <div style={{ position: 'relative' }}>
                  <textarea
                    value={newTipDescription}
                    onChange={(e) => setNewTipDescription(e.target.value)}
                    placeholder="Share your production tip, technique, or tutorial..."
                    maxLength={1000}
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      paddingRight: '3rem',
                      background: 'rgba(30, 20, 50, 0.5)',
                      border: '1px solid rgba(147, 51, 234, 0.3)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.9rem',
                      resize: 'vertical',
                      minHeight: '120px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowEmotePicker(!showEmotePicker)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '0.75rem',
                      background: 'transparent',
                      border: 'none',
                      color: showEmotePicker ? '#fbbf24' : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      padding: '0.25rem'
                    }}
                  >
                    <Smile size={20} />
                  </button>
                  <EmotePicker 
                    isOpen={showEmotePicker} 
                    onEmoteSelect={handleEmoteSelect} 
                    onClose={() => setShowEmotePicker(false)} 
                  />
                </div>
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                  {newTipDescription.length}/1000
                </span>
              </div>

              <div>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                  Type
                </label>
                <select
                  value={newTipType}
                  onChange={(e) => setNewTipType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'rgba(30, 20, 50, 0.5)',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="tutorial">Tutorial</option>
                  <option value="technique">Technique</option>
                  <option value="workflow">Workflow</option>
                  <option value="gear">Gear/Software</option>
                  <option value="mixing">Mixing</option>
                  <option value="mastering">Mastering</option>
                </select>
              </div>

              <div>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={newTipTags}
                  onChange={(e) => setNewTipTags(e.target.value)}
                  placeholder="e.g., Bass, Compression, Mixing"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'rgba(30, 20, 50, 0.5)',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  onClick={resetForm}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitNewTip}
                  disabled={submitting || !newTipTitle.trim() || !newTipDescription.trim()}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    border: 'none',
                    background: submitting || !newTipTitle.trim() || !newTipDescription.trim() 
                      ? 'rgba(147, 51, 234, 0.3)' 
                      : 'linear-gradient(135deg, #9333ea, #fbbf24)',
                    color: '#fff',
                    cursor: submitting || !newTipTitle.trim() || !newTipDescription.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Tip'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProTipsTab;
